import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import {
  CodingModel,
  DiagnosisChipView,
  GrantOptionRule,
  GuaranteeDecision,
  PackageCoverageMatchService,
  PrescriptionLineView,
  PrescriptionPackageCoverageWriteModel,
  V2PrescriptionCategoryModel,
  V2PrescriptionModel,
} from '@lib/guarantees/data-access';
import { setGuaranteeDecision } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { selectCreatedAtByGuaranteeId, selectDiagnosesByGuaranteeId, selectPackageIdByGuaranteeId } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { DECISION_ICON, DECISION_LABEL_KEY } from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { DECISION_ANSWERS, DECISION_BLOCKER_HINT_KEY, DecisionAnswer, decisionBlocker } from '@lib/guarantees/feature/components/common/prescription-decisions';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { of, take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../components/common/callout.component';
import { ControlDirective } from '../components/common/control.directive';
import { FieldLabelComponent } from '../components/common/field-label.component';
import { OptionPickerComponent, PickerOption } from '../components/common/option-picker.component';
import {
  PackageOptionPickerComponent,
  PackageOptionSelection,
  PackageOptionsStatus,
  selectionFromSnapshot,
  toCoverageWriteModel,
} from '../components/common/package-option-picker.component';
import { SelfPayDisclaimerComponent } from '../components/common/self-pay-disclaimer.component';
import { ToggleDirective } from '../components/common/toggle.directive';
import { DIALOG_CLASSES, FIELD_MESSAGE_CLASS, SECTION_LABEL_CLASS } from '../components/ui/ui.classes';
import { CONFIRM_CLASS } from './confirm-dialog.component';
import { DECLINE_REASONS, GUARANTEE_REASONS } from './decision-reasons';

/**
 * The grounds each answer is recorded under — one list per answer, never shared. Granting and
 * refusing are different acts, and neither is the amendment vocabulary the edit and delete flows
 * use, so a wording offered under one of them must never be offered under another.
 */
const REASONS: Record<DecisionAnswer, string[]> = {
  [GuaranteeDecision.Guaranteed]: GUARANTEE_REASONS,
  [GuaranteeDecision.NotGuaranteed]: DECLINE_REASONS,
};

const REASON_LABEL_KEY: Record<DecisionAnswer, string> = {
  [GuaranteeDecision.Guaranteed]: 'guaranteeV2.label.guaranteeReason',
  [GuaranteeDecision.NotGuaranteed]: 'guaranteeV2.label.declineReason',
};

const REASON_PLACEHOLDER_KEY: Record<DecisionAnswer, string> = {
  [GuaranteeDecision.Guaranteed]: 'guaranteeV2.message.guaranteeReasonPlaceholder',
  [GuaranteeDecision.NotGuaranteed]: 'guaranteeV2.message.declineReasonPlaceholder',
};

export interface DecisionFormData {
  /** Names what the ruling was opened over, so the dialog is not headed "line" over a whole visit. */
  titleKey: string;
  /** Preselected answer, where the flow was entered from a control that already names one. */
  decision?: DecisionAnswer;
  /** Everything the ruling was opened on — a line, a course, or a whole visit's worth. */
  lines: PrescriptionLineView[];
  /** The record's diagnoses — what the package options' diagnoses are matched against. */
  diagnoses?: DiagnosisChipView[];
  /** The day whose package content applies: when the guarantee was raised. */
  asOf?: string;
  /** The package the guarantee was raised under. */
  guaranteePackageId?: string | null;
}

export interface DecisionFormResult {
  decision: DecisionAnswer;
  /** The wording as a coding carrying no code: there is no dictionary of reasons yet. */
  reason: { name: string };
  note?: string;
  /**
   * Exactly the lines the answer may be applied to — never all the ones it was opened on — split by the
   * package option each set is granted under. One group, without an option, for a refusal or a member
   * without a flexible package.
   */
  groups: DecisionWriteGroup[];
}

export interface DecisionWriteGroup {
  prescriptionIds: string[];
  coverage?: PrescriptionPackageCoverageWriteModel;
}

/** Lines matched to the package options alike, with the option chosen for them. */
interface OptionGroupView {
  key: string;
  prescriptionIds: string[];
  names: string[];
  diagnosis?: CodingModel;
  categories: V2PrescriptionCategoryModel[];
  /** Some line carries no option yet, so the group has to be given one. */
  uncovered: boolean;
  selection: PackageOptionSelection | null;
  missing: boolean;
}

/** One line as the flow reports it: whether this answer applies to it, and why not when it doesn't. */
interface DecisionLineView {
  id: string;
  name: string;
  included: boolean;
  blockerKey: string;
}

/** Which record is being ruled on, and which of its lines the ruling was opened over. */
export interface DecisionFormContext {
  guaranteeId: string;
  lines: PrescriptionLineView[];
  /** Names the scope the ruling was opened at — a line, a course, or the whole visit. */
  titleKey: string;
  /** Set where the control that opened the flow already names an answer, as the triage buttons do. */
  decision?: DecisionAnswer;
}

/**
 * Opens the ruling and carries its answer into the store. Held beside the form rather than in each
 * caller: a line's menu, a course's menu, the visit header and the work list must all rule the same
 * way, and all four dispatch one write however many lines it names.
 */
export function openDecisionForm(dialog: MatDialog, store: Store, context: DecisionFormContext): void {
  const data: DecisionFormData = {
    titleKey: context.titleKey,
    decision: context.decision,
    lines: context.lines,
    diagnoses: store.selectSignal(selectDiagnosesByGuaranteeId)().get(context.guaranteeId),
    asOf: store.selectSignal(selectCreatedAtByGuaranteeId)().get(context.guaranteeId),
    guaranteePackageId: store.selectSignal(selectPackageIdByGuaranteeId)().get(context.guaranteeId),
  };

  dialog
    .open<DecisionFormComponent, DecisionFormData, DecisionFormResult>(DecisionFormComponent, {
      width: '90vw',
      maxWidth: '520px',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    })
    .afterClosed()
    .pipe(take(1))
    .subscribe(result => {
      if (!result) {
        return;
      }

      store.dispatch(
        setGuaranteeDecision({
          guaranteeId: context.guaranteeId,
          groups: result.groups,
          decision: result.decision,
          reason: result.reason,
          note: result.note,
        })
      );
    });
}

/**
 * One ruling with both answers on it, chosen against the lines it would apply to — which answer is
 * open depends on where each line stands, so the flow states line by line what it would leave
 * untouched rather than presenting a count that hides it.
 *
 * Both answers take grounds from a closed list: two managers ruling the same way in different words
 * look like two different rulings to anyone reporting on them afterwards.
 */
@Component({
  selector: 'guarantees-decision-form',
  standalone: true,
  imports: [
    FormsModule,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    CalloutComponent,
    ControlDirective,
    FieldLabelComponent,
    OptionPickerComponent,
    PackageOptionPickerComponent,
    SelfPayDisclaimerComponent,
    ToggleDirective,
  ],
  templateUrl: './decision-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecisionFormComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  /** Holds a line under the control it belongs to, so a message never reflows the form. */
  protected readonly fieldMessage = FIELD_MESSAGE_CLASS;

  private readonly dialogRef = inject(MatDialogRef<DecisionFormComponent, DecisionFormResult>);

  protected readonly data = inject<DecisionFormData>(MAT_DIALOG_DATA);

  /** Names the block of lines the ruling would act on, at the weight the panels label facts. */
  protected readonly sectionLabelClass = SECTION_LABEL_CLASS;

  protected readonly answers = DECISION_ANSWERS;
  protected readonly decisionIcon = DECISION_ICON;
  protected readonly decisionLabelKey = DECISION_LABEL_KEY;

  protected readonly decision = signal<DecisionAnswer>(this.data.decision ?? GuaranteeDecision.Guaranteed);
  protected readonly reason = signal('');
  protected readonly note = signal('');

  protected readonly isDecline = computed(() => this.decision() === GuaranteeDecision.NotGuaranteed);

  /** The confirm dialog's own tone classes: submitting a ruling is the same act at the same weight. */
  protected readonly submitClass = computed(() => CONFIRM_CLASS[this.isDecline() ? 'error' : 'success']);

  protected readonly reasonLabelKey = computed(() => REASON_LABEL_KEY[this.decision()]);
  protected readonly reasonPlaceholderKey = computed(() => REASON_PLACEHOLDER_KEY[this.decision()]);
  protected readonly reasonOptions = computed<PickerOption[]>(() => REASONS[this.decision()].map(value => ({ value })));

  /**
   * What the chosen answer would do to each line it was opened on. Deleted lines are left out
   * rather than listed as excluded: they are not part of the care plan, so they are not something
   * this ruling declined to act on — they are not what it is about at all.
   */
  protected readonly lines = computed<DecisionLineView[]>(() => {
    const decision = this.decision();

    return this.data.lines
      .filter(line => !line.prescription.isDeleted)
      .map(line => {
        const blocker = decisionBlocker(line, decision);

        return {
          id: line.prescription.id,
          name: line.prescription.name?.trim() || line.prescription.code,
          included: !blocker,
          blockerKey: blocker ? DECISION_BLOCKER_HINT_KEY[blocker] : '',
        };
      });
  });

  protected readonly included = computed(() => this.lines().filter(line => line.included));

  /** The lines the answer would be applied to, as records. */
  private readonly targets = computed(() => {
    const ids = new Set(this.included().map(line => line.id));
    return this.data.lines.filter(line => ids.has(line.prescription.id)).map(line => line.prescription);
  });

  /** The member (by user id) whose package options are offered — every line of one record is the same member's. */
  protected readonly memberUserId = this.data.lines[0]?.prescription.userId ?? '';

  /** What a grant has to name, as the service checks it; unknown while asked. */
  private readonly grantOptionRule = toSignal(
    this.memberUserId ? inject(PackageCoverageMatchService).grantOptionRule(this.memberUserId, this.data.guaranteePackageId) : of<GrantOptionRule>('none')
  );

  /** Whether the member is on a flexible package with options to choose; unknown while asked. */
  protected readonly flexible = computed(() => {
    const rule = this.grantOptionRule();
    return rule === undefined ? undefined : rule === 'required';
  });

  /** A grant on a flexible package is drawn against one of its options. */
  protected readonly offersOption = computed(() => !this.isDecline() && this.included().length > 0 && !!this.flexible());

  /** A ruling that names no option keeps the one a line already carries, so only a line without one has to be given one. */
  protected readonly someTargetUncovered = computed(() => this.targets().some(prescription => !prescription.coverage));

  /** Chosen per group, by group key; a group not yet touched starts from the option its lines already share. */
  private readonly selections = signal<Record<string, PackageOptionSelection | null>>({});
  private readonly optionsStatuses = signal<Record<string, PackageOptionsStatus>>({});

  /**
   * The lines split by what the package options are matched on — the diagnosis and the categories — so each
   * group is given an option that actually fits it. A course is always one group.
   */
  protected readonly optionGroups = computed<OptionGroupView[]>(() => {
    const byKey = new Map<string, V2PrescriptionModel[]>();
    this.targets().forEach(prescription => {
      const key = optionGroupKey(prescription);
      byKey.set(key, [...(byKey.get(key) ?? []), prescription]);
    });

    const selections = this.selections();
    const statuses = this.optionsStatuses();

    return [...byKey].map(([key, prescriptions]) => {
      const diagnosis = this.data.diagnoses?.find(item => item.id === prescriptions[0].diagnosisId)?.icd10;
      const uncovered = prescriptions.some(prescription => !prescription.coverage);
      const selection = key in selections ? selections[key] : sharedSelection(prescriptions);

      return {
        key,
        prescriptionIds: prescriptions.map(prescription => prescription.id),
        names: prescriptions.map(prescription => prescription.name?.trim() || prescription.code),
        diagnosis,
        categories: prescriptions[0].categories ?? [],
        uncovered,
        selection,
        missing: uncovered && this.flexible() !== false && (statuses[key] ?? 'loading') !== 'none' && !selection,
      };
    });
  });

  /** On a flexible package a grant names its option, unless the package has none; held while that is still being asked. */
  protected readonly optionMissing = computed(() => !this.isDecline() && this.optionGroups().some(group => group.missing));

  /** A line without an option cannot be granted on a package that has none to name. */
  protected readonly grantImpossible = computed(() => !this.isDecline() && this.someTargetUncovered() && this.grantOptionRule() === 'impossible');

  protected readonly submitBlockKey = computed(() => {
    if (this.included().length === 0) {
      return 'guaranteeV2.message.decisionNoTargets';
    }
    if (this.grantImpossible()) {
      return 'guaranteeV2.message.packageHasNoOptions';
    }
    if (this.optionMissing()) {
      return 'guaranteeV2.message.processOptionRequired';
    }

    return this.reason().trim() ? '' : 'guaranteeV2.message.decisionReasonRequired';
  });

  /** Either answer states its grounds: a ruling nobody gave a reason for cannot be reviewed. */
  protected readonly canSubmit = computed(() => !this.submitBlockKey());

  protected setDecision(decision: DecisionAnswer): void {
    if (decision === this.decision()) {
      return;
    }

    this.decision.set(decision);
    // The grounds belong to the answer that was given, so switching answers clears them rather than
    // carrying a refusal's wording onto a grant.
    this.reason.set('');
  }

  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    const note = this.note().trim();

    this.dialogRef.close({
      decision: this.decision(),
      reason: { name: this.reason().trim() },
      note: note || undefined,
      groups: this.offersOption() ? decisionGroups(this.optionGroups()) : [{ prescriptionIds: this.included().map(line => line.id) }],
    });
  }

  protected setSelection(key: string, selection: PackageOptionSelection | null): void {
    this.selections.update(current => ({ ...current, [key]: selection }));
  }

  protected setOptionsStatus(key: string, status: PackageOptionsStatus): void {
    this.optionsStatuses.update(current => ({ ...current, [key]: status }));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}

/** The lines' option groups as written: groups ending up with the same choice go in one call. */
function decisionGroups(groups: OptionGroupView[]): DecisionWriteGroup[] {
  const byChoice = new Map<string, DecisionWriteGroup>();
  groups.forEach(group => {
    const coverage = group.selection ? toCoverageWriteModel(group.selection) : undefined;
    const key = coverage ? JSON.stringify(coverage) : '';
    const merged = byChoice.get(key);
    byChoice.set(key, { prescriptionIds: [...(merged?.prescriptionIds ?? []), ...group.prescriptionIds], coverage });
  });

  return [...byChoice.values()];
}

/** Lines with the same diagnosis and the same categories are matched to the package options alike. */
function optionGroupKey(prescription: V2PrescriptionModel): string {
  const categories = (prescription.categories ?? []).map(category => category.categoryCode).sort((first, second) => first - second);
  return `${prescription.diagnosisId ?? ''}|${categories.join(',')}`;
}

function sharedSelection(prescriptions: V2PrescriptionModel[]): PackageOptionSelection | null {
  const selections = prescriptions.map(prescription => selectionFromSnapshot(prescription.coverage));
  const first = selections[0];
  const key = (selection: PackageOptionSelection | null) => (selection ? JSON.stringify(toCoverageWriteModel(selection)) : '');

  return first && selections.every(selection => key(selection) === key(first)) ? first : null;
}
